/**
 * Responsibility: Implement commerce store behavior for entitlement, usage, and marketplace ownership state.
 * Scope: UI store orchestration only; contract normalization and runtime persistence stay elsewhere.
 */
import { authStore } from './auth-store'
import type {
  CommercePlan,
  CommerceTesterGrant,
  EntitlementSnapshot,
  ManagedUsageEntry,
  ManagedUsageSummary,
  MarketplaceArtifactReceipt,
  PreforgedOffer,
} from '../../commerce/commerce-contract'
import { DEFAULT_COMMERCE_PLANS, readLocalCommercePlans } from '../../commerce/commerce-contract'
import {
  listAllManagedUsage,
  listCurrentTesterGrants,
  listCurrentMarketplaceReceipts,
  listPublishedPreforgedOffers,
  resolveCurrentEntitlement,
  summarizeCurrentManagedUsage,
} from '../../commerce/commerce-runtime'

export interface CommerceSnapshot {
  loading: boolean
  plans: CommercePlan[]
  entitlement: EntitlementSnapshot
  usage: ManagedUsageSummary
  cohortUsage: ManagedUsageEntry[]
  receipts: MarketplaceArtifactReceipt[]
  testerGrants: CommerceTesterGrant[]
  preforgedOffers: PreforgedOffer[]
  error: string
}

const subscribers = new Set<() => void>()
let refreshPromise: Promise<CommerceSnapshot> | null = null
let snapshotValue: CommerceSnapshot = {
  loading: true,
  plans: [...DEFAULT_COMMERCE_PLANS],
  entitlement: {
    planId: 'free',
    active: false,
    hostedProxyAccess: false,
    managedImages: false,
    creatorConsoleAccess: false,
    preforgedRealityAccess: false,
    managedActionQuota: 0,
    managedImageQuota: 0,
    creatorPublishSlots: 0,
    source: 'default',
  },
  usage: {
    managedActionQuota: 0,
    managedImageQuota: 0,
    managedActionUsed: 0,
    managedImageUsed: 0,
    managedActionRemaining: 0,
    managedImageRemaining: 0,
    byokActionCount: 0,
    byokImageCount: 0,
    localActionCount: 0,
    localImageCount: 0,
  },
  cohortUsage: [],
  receipts: [],
  testerGrants: [],
  preforgedOffers: [],
  error: '',
}

function emit(): void {
  for (const fn of subscribers) fn()
}

async function refreshSnapshot(): Promise<CommerceSnapshot> {
  const plans = readLocalCommercePlans()
  try {
    const entitlement = await resolveCurrentEntitlement(plans)
    const usage = await summarizeCurrentManagedUsage(entitlement, plans).catch(() => snapshotValue.usage)
    const receipts = await listCurrentMarketplaceReceipts().catch(() => [])
    const testerGrants = await listCurrentTesterGrants().catch(() => [])
    const preforgedOffers = await listPublishedPreforgedOffers().catch(() => [])
    const cohortUsage = await listAllManagedUsage().catch(() => [])
    snapshotValue = {
      loading: false,
      plans,
      entitlement,
      usage,
      cohortUsage,
      receipts,
      testerGrants,
      preforgedOffers,
      error: '',
    }
  } catch (error) {
    snapshotValue = {
      ...snapshotValue,
      loading: false,
      plans,
      error: String(error).slice(0, 160),
    }
  }
  emit()
  return snapshotValue
}

function queueRefresh(): Promise<CommerceSnapshot> {
  if (!refreshPromise) {
    snapshotValue = { ...snapshotValue, loading: true }
    emit()
    refreshPromise = refreshSnapshot().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

authStore.subscribe(() => {
  void queueRefresh()
})
void queueRefresh()

export const commerceStore = {
  get snapshot(): CommerceSnapshot {
    return snapshotValue
  },

  get entitlement(): EntitlementSnapshot {
    return snapshotValue.entitlement
  },

  get usage(): ManagedUsageSummary {
    return snapshotValue.usage
  },

  get receipts(): MarketplaceArtifactReceipt[] {
    return snapshotValue.receipts
  },

  get cohortUsage(): ManagedUsageEntry[] {
    return snapshotValue.cohortUsage
  },

  get testerGrants(): CommerceTesterGrant[] {
    return snapshotValue.testerGrants
  },

  get preforgedOffers(): PreforgedOffer[] {
    return snapshotValue.preforgedOffers
  },

  subscribe(fn: () => void): () => void {
    subscribers.add(fn)
    return () => subscribers.delete(fn)
  },

  refresh(): Promise<CommerceSnapshot> {
    return queueRefresh()
  },
}
